import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Vendor {
  vendorId: string;
  vendorName: string;
  companyName: string;
  databaseName: string;
  isActive: boolean;
}

interface VendorContextType {
  currentVendor: Vendor | null;
  vendors: Vendor[];
  setCurrentVendor: (vendor: Vendor | null) => void;
  loadVendors: () => Promise<void>;
  switchVendor: (vendor: Vendor) => void;
}

const VendorContext = createContext<VendorContextType | undefined>(undefined);

export const useVendor = () => {
  const context = useContext(VendorContext);
  if (context === undefined) {
    throw new Error('useVendor must be used within a VendorProvider');
  }
  return context;
};

interface VendorProviderProps {
  children: ReactNode;
}

export const VendorProvider: React.FC<VendorProviderProps> = ({ children }) => {
  const [currentVendor, setCurrentVendor] = useState<Vendor | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);

  const loadVendors = async () => {
    try {
      // Check if user is admin or vendor
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (user.role === 'super_admin') {
        // Super admin users can load all vendors
        const response = await fetch(`${process.env.REACT_APP_API_URL}/vendors?limit=100`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const vendors: Vendor[] = data.data.map((vendor: any) => ({
            vendorId: vendor.vendorId,
            vendorName: vendor.name,
            companyName: vendor.companyName,
            databaseName: vendor.databaseName,
            isActive: vendor.isActive
          }));
          setVendors(vendors);
          
          // Load current vendor from localStorage
          const savedVendor = localStorage.getItem('currentVendor');
          if (savedVendor) {
            const vendor = JSON.parse(savedVendor);
            setCurrentVendor(vendor);
          } else if (vendors.length > 0) {
            // Set first vendor as default
            setCurrentVendor(vendors[0]);
            localStorage.setItem('currentVendor', JSON.stringify(vendors[0]));
          }
        } else {
          // Fallback to mock data if API fails
          console.warn('API failed, using mock data');
          const mockVendors: Vendor[] = [
            {
              vendorId: 'vendor1',
              vendorName: 'مكتب الحرمين',
              companyName: 'مكتب الحرمين',
              databaseName: 'hajj_vendor1',
              isActive: true
            },
            {
              vendorId: 'vendor2',
              vendorName: 'مكتب الرحمة',
              companyName: 'مكتب الرحمة',
              databaseName: 'hajj_vendor2',
              isActive: true
            }
          ];
          setVendors(mockVendors);
          
          const savedVendor = localStorage.getItem('currentVendor');
          if (savedVendor) {
            const vendor = JSON.parse(savedVendor);
            setCurrentVendor(vendor);
          } else if (mockVendors.length > 0) {
            setCurrentVendor(mockVendors[0]);
            localStorage.setItem('currentVendor', JSON.stringify(mockVendors[0]));
          }
        }
      } else {
        // Vendor users should only see their own vendor info
        const savedVendor = localStorage.getItem('currentVendor');
        if (savedVendor) {
          const vendor = JSON.parse(savedVendor);
          setCurrentVendor(vendor);
          setVendors([vendor]); // Only show their own vendor
        }
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
      // Fallback to mock data
      const mockVendors: Vendor[] = [
        {
          vendorId: 'vendor1',
          vendorName: 'مكتب الحرمين',
          companyName: 'مكتب الحرمين',
          databaseName: 'hajj_vendor1',
          isActive: true
        },
        {
          vendorId: 'vendor2',
          vendorName: 'مكتب الرحمة',
          companyName: 'مكتب الرحمة',
          databaseName: 'hajj_vendor2',
          isActive: true
        }
      ];
      setVendors(mockVendors);
      
      const savedVendor = localStorage.getItem('currentVendor');
      if (savedVendor) {
        const vendor = JSON.parse(savedVendor);
        setCurrentVendor(vendor);
      } else if (mockVendors.length > 0) {
        setCurrentVendor(mockVendors[0]);
        localStorage.setItem('currentVendor', JSON.stringify(mockVendors[0]));
      }
    }
  };

  const setCurrentVendorWithStorage = (vendor: Vendor | null) => {
    setCurrentVendor(vendor);
    if (vendor) {
      localStorage.setItem('currentVendor', JSON.stringify(vendor));
    } else {
      localStorage.removeItem('currentVendor');
    }
  };

  const switchVendor = (vendor: Vendor) => {
    setCurrentVendorWithStorage(vendor);
  };

  useEffect(() => {
    loadVendors();
  }, []);

  const value: VendorContextType = {
    currentVendor,
    vendors,
    setCurrentVendor: setCurrentVendorWithStorage,
    loadVendors,
    switchVendor
  };

  return (
    <VendorContext.Provider value={value}>
      {children}
    </VendorContext.Provider>
  );
}; 